import React from "react";
import { Link } from "@/components/common/Link";
export default function Comparison() {
  return <section id="comparison" aria-labelledby="comparison-heading" className="bg-background text-foreground py-20 lg:py-28 border-b border-border/40">
      <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mb-12 sm:mb-14">
          <p className="text-xs sm:text-sm font-semibold uppercase tracking-wider text-primary mb-3">
            Choosing Your Setup
          </p>
          <h2 id="comparison-heading" className="font-serif text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-foreground leading-tight">
            Freelance SEO Consultant vs SEO Agency vs In-House Hire
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            The right choice depends on your business stage, budget and current resources, as each setup has distinct strengths.
          </p>
        </div>

        {/* Semantic Comparison Table with Horizontal Scroll on Mobile */}
        <div className="border border-border rounded-xl bg-card overflow-hidden shadow-xs">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[640px] text-left border-collapse">
              <caption className="sr-only">
                Comparison of Freelance SEO Consultant, SEO Agency, and In-House SEO Hire across delivery model, communication, flexibility, and suitability
              </caption>
              <thead>
                <tr className="border-b border-border text-sm sm:text-base">
                  <th scope="col" className="p-4 sm:p-5 w-1/4 font-semibold text-muted-foreground">
                    Dimension
                  </th>
                  <th scope="col" className="p-4 sm:p-5 w-1/4 font-bold text-foreground bg-primary/5 border-x border-border/60">
                    Freelance SEO consultant
                  </th>
                  <th scope="col" className="p-4 sm:p-5 w-1/4 font-semibold text-foreground">
                    SEO agency
                  </th>
                  <th scope="col" className="p-4 sm:p-5 w-1/4 font-semibold text-foreground">
                    In-house SEO hire
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border/60 text-sm sm:text-base leading-relaxed">
                <tr>
                  <th scope="row" className="p-4 sm:p-5 font-medium text-foreground align-top">
                    Who does the work
                  </th>
                  <td className="p-4 sm:p-5 align-top bg-primary/5 border-x border-border/60 text-foreground font-normal">
                    Typically one senior specialist who evaluates your site, plans the strategy and carries out the recommendations directly.
                  </td>
                  <td className="p-4 sm:p-5 align-top text-muted-foreground">
                    Often a team with specialist disciplines, though daily execution can be distributed across several individuals or account tiers.
                  </td>
                  <td className="p-4 sm:p-5 align-top text-muted-foreground">
                    A dedicated employee fully embedded in your day-to-day operations and internal meetings.
                  </td>
                </tr>
                <tr>
                  <th scope="row" className="p-4 sm:p-5 font-medium text-foreground align-top">
                    How you communicate
                  </th>
                  <td className="p-4 sm:p-5 align-top bg-primary/5 border-x border-border/60 text-foreground font-normal">
                    Direct communication with the person doing the work, with concise written briefs and no account management layers.
                  </td>
                  <td className="p-4 sm:p-5 align-top text-muted-foreground">
                    Typically managed through an account manager or client coordinator who routes messages to production specialists.
                  </td>
                  <td className="p-4 sm:p-5 align-top text-muted-foreground">
                    Direct, everyday team communication across your internal channels, standups and meetings.
                  </td>
                </tr>
                <tr>
                  <th scope="row" className="p-4 sm:p-5 font-medium text-foreground align-top">
                    Flexibility
                  </th>
                  <td className="p-4 sm:p-5 align-top bg-primary/5 border-x border-border/60 text-foreground font-normal">
                    High flexibility: can be engaged for a standalone audit, a focused project or ongoing advisory support that scales as priorities evolve.
                  </td>
                  <td className="p-4 sm:p-5 align-top text-muted-foreground">
                    Often tied to fixed multi-month retainer packages with structured minimum scopes or bundled multi-service agreements.
                  </td>
                  <td className="p-4 sm:p-5 align-top text-muted-foreground">
                    A fixed ongoing payroll commitment with recruitment overheads, equipment provision and management requirements.
                  </td>
                </tr>
                <tr>
                  <th scope="row" className="p-4 sm:p-5 font-medium text-foreground align-top">
                    Best suited to
                  </th>
                  <td className="p-4 sm:p-5 align-top bg-primary/5 border-x border-border/60 text-foreground font-normal">
                    Businesses and in-house marketing teams that want direct senior expertise, tailored roadmaps and focused commercial execution without agency overheads.
                  </td>
                  <td className="p-4 sm:p-5 align-top text-muted-foreground">
                    Organisations wanting a large, multi-channel team handling paid media, creative production, PR and search under one roof.
                  </td>
                  <td className="p-4 sm:p-5 align-top text-muted-foreground">
                    Larger enterprises with continuous, full-time search workflows that justify an internal salary and dedicated internal management.
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Informative follow-up links */}
        <div className="mt-8 text-sm sm:text-base text-muted-foreground">
          <p>
            Weighing it up? Read about an{" "}
            <Link to="/SEOAgencyAlternativeUkPage" className="font-medium text-primary underline underline-offset-4 hover:text-primary/80 transition-colors">
              SEO agency alternative in the UK
            </Link>
            , a{" "}
            <Link to="/FractionalSeoConsultantPage" className="font-medium text-primary underline underline-offset-4 hover:text-primary/80 transition-colors">
              fractional SEO consultant
            </Link>
            , or how to{" "}
            <Link to="/HireSeoConsultantPage" className="font-medium text-primary underline underline-offset-4 hover:text-primary/80 transition-colors">
              hire an SEO consultant
            </Link>
            .
          </p>
        </div>
      </div>
    </section>;
}